import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { environment } from "environment/environment";
import MerchantEmptyState from "./MerchantEmptyState";
import MerchantLoader from "./MerchantLoader";
import { isLiveMerchantPlatform } from "./merchantBranding";
import { IconRefresh, MerchantOrder, OrderSheet } from "./MerchantOrders";

export interface MerchantNotificationItem {
  id: string;
  event: string;
  title: string;
  message: string;
  tone: "ok" | "pending" | "warn" | "muted";
  read: boolean;
  createdAt?: string | null;
  zuluOrderId?: string | null;
  zuluUniqueId?: string | null;
  shopifyOrderNumber?: string | null;
  customerName?: string | null;
}

type NoticeFilter = "all" | "unread";

interface MerchantNotificationsProps {
  platform: string;
  shop: string;
  session: string;
  refreshKey?: number;
  onUnreadChange?: (count: number) => void;
  onViewOrders?: () => void;
  onViewOrder?: (order: MerchantOrder) => void;
}

const FILTERS: Array<{ id: NoticeFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "unread", label: "Unread" },
];

const COMPLETION = new Set(["ORDER_DELIVERED", "ARRIVED_EARLY"]);

const MerchantNotifications: React.FC<MerchantNotificationsProps> = ({
  platform,
  shop,
  session,
  refreshKey = 0,
  onUnreadChange,
  onViewOrders,
  onViewOrder,
}) => {
  const [items, setItems] = useState<MerchantNotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState<NoticeFilter>("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState<MerchantOrder | null>(null);
  const itemsRef = useRef<MerchantNotificationItem[]>([]);
  const readyRef = useRef(false);
  const requestId = useRef(0);
  const onUnreadChangeRef = useRef(onUnreadChange);
  onUnreadChangeRef.current = onUnreadChange;

  const notificationsUrl = useCallback(() => {
    const params = new URLSearchParams({ session });
    if (shop) {
      params.set("shop", shop);
    }
    return `${environment.api_url}/api/v1/integrations/${platform}/notifications?${params.toString()}`;
  }, [platform, shop, session]);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!session || !isLiveMerchantPlatform(platform)) {
      setLoading(false);
      return;
    }
    const silent = Boolean(options?.silent) && readyRef.current;
    const nextRequest = requestId.current + 1;
    requestId.current = nextRequest;
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      const response = await fetch(notificationsUrl());
      const body = await response.json();
      if (nextRequest !== requestId.current) {
        return;
      }
      if (!response.ok || body.success === false) {
        if (silent) {
          return;
        }
        setError(body.error || body.message || "Could not load notifications.");
        setItems([]);
        itemsRef.current = [];
        setUnreadCount(0);
        if (onUnreadChangeRef.current) {
          onUnreadChangeRef.current(0);
        }
        return;
      }
      const nextItems = Array.isArray(body.data && body.data.items) ? body.data.items : [];
      const nextUnread =
        typeof body.data?.unreadCount === "number" ? body.data.unreadCount : 0;
      itemsRef.current = nextItems;
      readyRef.current = true;
      setItems(nextItems);
      setUnreadCount(nextUnread);
      if (onUnreadChangeRef.current) {
        onUnreadChangeRef.current(nextUnread);
      }
      setError("");
    } catch {
      if (nextRequest !== requestId.current || silent) {
        return;
      }
      setError("Could not load notifications. Try again.");
      setItems([]);
      itemsRef.current = [];
    } finally {
      if (nextRequest === requestId.current) {
        setLoading(false);
      }
    }
  }, [notificationsUrl, platform, session]);

  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!refreshKey) {
      return;
    }
    loadRef.current({ silent: true });
  }, [refreshKey]);

  const visible = useMemo(
    () =>
      items.filter(
        (item) => COMPLETION.has(item.event) && matchesFilter(item, filter)
      ),
    [items, filter]
  );

  const markRead = async (item: MerchantNotificationItem) => {
    if (item.read) {
      return;
    }
    setItems((current) =>
      current.map((row) => (row.id === item.id ? { ...row, read: true } : row))
    );
    setUnreadCount((current) => Math.max(0, current - 1));
    if (onUnreadChange) {
      onUnreadChange(Math.max(0, unreadCount - 1));
    }
    try {
      const params = new URLSearchParams({ session });
      if (shop) {
        params.set("shop", shop);
      }
      const response = await fetch(
        `${environment.api_url}/api/v1/integrations/${platform}/notifications/${item.id}/read?${params.toString()}`,
        { method: "POST" }
      );
      if (!response.ok) {
        throw new Error("read failed");
      }
    } catch {
      setItems((current) =>
        current.map((row) => (row.id === item.id ? { ...row, read: false } : row))
      );
      setUnreadCount((current) => current + 1);
    }
  };

  const markAllRead = async () => {
    if (!unreadCount || busy) {
      return;
    }
    const previous = items;
    const previousUnread = unreadCount;
    setBusy(true);
    setItems((current) => current.map((row) => ({ ...row, read: true })));
    setUnreadCount(0);
    if (onUnreadChange) {
      onUnreadChange(0);
    }
    try {
      const params = new URLSearchParams({ session });
      if (shop) {
        params.set("shop", shop);
      }
      const response = await fetch(
        `${environment.api_url}/api/v1/integrations/${platform}/notifications/read-all?${params.toString()}`,
        { method: "POST" }
      );
      if (!response.ok) {
        throw new Error("read-all failed");
      }
    } catch {
      setItems(previous);
      setUnreadCount(previousUnread);
      if (onUnreadChange) {
        onUnreadChange(previousUnread);
      }
    } finally {
      setBusy(false);
    }
  };

  const openItem = (item: MerchantNotificationItem) => {
    markRead(item);
    if (!item.zuluOrderId && !item.zuluUniqueId) {
      return;
    }
    const nextOrder: MerchantOrder = {
      zuluOrderId: item.zuluOrderId,
      zuluUniqueId: item.zuluUniqueId,
      shopifyOrderNumber: item.shopifyOrderNumber,
      customerName: item.customerName,
    };
    if (onViewOrder) {
      onViewOrder(nextOrder);
      return;
    }
    setViewing(nextOrder);
  };

  return (
    <section className="zulu-plugin__stage-body">
      <div className="zulu-plugin__orders">
        <div className="zulu-plugin__orders-head">
          <div>
            <h2>Order updates</h2>
            <p>
              {loading
                ? "Loading delivery updates…"
                : unreadCount
                ? `${unreadCount} unread ${unreadCount === 1 ? "update" : "updates"}`
                : "Delivered and dropped orders for this store"}
            </p>
          </div>
          <div className="zulu-plugin__notice-actions">
            <button
              type="button"
              className="zulu-plugin__refresh-btn"
              onClick={() => load()}
              disabled={loading}
              aria-busy={loading}
              aria-label="Refresh notifications"
            >
              <IconRefresh />
              <span>{loading ? "Refreshing…" : "Refresh"}</span>
            </button>
            <button
              type="button"
              onClick={markAllRead}
              disabled={busy || !unreadCount}
            >
              Mark all read
            </button>
          </div>
        </div>
        <div className="zulu-plugin__status-chips" role="tablist" aria-label="Notification filters">
          {FILTERS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`zulu-plugin__filter-chip${filter === item.id ? " on" : ""}`}
              onClick={() => setFilter(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        {error && items.length ? <p className="zulu-plugin__orders-error">{error}</p> : null}
        {loading && !items.length ? (
          <MerchantLoader size="panel" label="Loading order updates…" />
        ) : error && !items.length ? (
          <MerchantEmptyState
            kind="error"
            title="Could not load notifications"
            description={error}
            actionLabel="Try again"
            onAction={() => load()}
          />
        ) : !visible.length ? (
          <MerchantEmptyState
            kind="notifications"
            title={filter === "unread" ? "No unread updates" : "You're all caught up"}
            description={
              filter === "all"
                ? "Delivered and dropped orders for this store will show here."
                : "Nothing matches this filter."
            }
            actionLabel={onViewOrders ? "View orders" : undefined}
            onAction={onViewOrders}
          />
        ) : (
          <ul className="zulu-plugin__notice-list">
            {visible.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={`zulu-plugin__notice${item.read ? " is-read" : ""} is-${item.tone}`}
                  onClick={() => openItem(item)}
                >
                  <span className="zulu-plugin__notice-dot" aria-hidden="true" />
                  <span className="zulu-plugin__notice-copy">
                    <strong>{item.title}</strong>
                    <small>
                      {item.shopifyOrderNumber ? `#${item.shopifyOrderNumber}` : item.zuluUniqueId}{" "}
                      · {item.message}
                    </small>
                    <em>{formatNoticeTime(item.createdAt)}</em>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {onViewOrder ? null : (
        <OrderSheet
          order={viewing}
          onClose={() => setViewing(null)}
          labelContext={{ platform, shop, session }}
        />
      )}
    </section>
  );
};

const matchesFilter = (
  item: MerchantNotificationItem,
  filter: NoticeFilter
): boolean => {
  if (filter === "unread") {
    return !item.read;
  }
  return true;
};

const formatNoticeTime = (value?: string | null): string => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export default MerchantNotifications;
